import { QueryClient } from '@tanstack/react-query';

import { ApiError } from './api';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Content changes when a person saves it, not on a timer. Refetching on
      // every window focus makes a dashboard feel restless and buys nothing.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Retrying a 401 fights the refresh interceptor; retrying a 403, a 404
        // or a validation failure just asks the same wrong question again.
        if (error instanceof ApiError) {
          if (error.status >= 400 && error.status < 500) return false;
        }
        return failureCount < 2;
      },
    },
    mutations: {
      // A mutation that failed on a version conflict must never be replayed —
      // the second attempt would carry the same stale version.
      retry: false,
    },
  },
});

/** One place for cache keys, so an invalidation cannot miss a list. */
export const qk = {
  me: ['me'] as const,
  dashboard: ['dashboard'] as const,
  readiness: ['readiness'] as const,
  auditLogs: (params: unknown) => ['audit-logs', params] as const,
  categories: (params: unknown) => ['categories', params] as const,
  category: (id: string) => ['category', id] as const,
  items: (params: unknown) => ['items', params] as const,
  item: (id: string) => ['item', id] as const,
  addonGroups: (params: unknown) => ['addon-groups', params] as const,
  addonGroup: (id: string) => ['addon-group', id] as const,
  addonOptions: (groupId: string) => ['addon-options', groupId] as const,
  addonOption: (id: string) => ['addon-option', id] as const,
  offers: (params: unknown) => ['offers', params] as const,
  offer: (id: string) => ['offer', id] as const,
  offerConflicts: (itemId: string) => ['offer-conflicts', itemId] as const,
  branches: (params: unknown) => ['branches', params] as const,
  branch: (id: string) => ['branch', id] as const,
  branchPreview: (id: string) => ['branch-preview', id] as const,
  closures: (id: string) => ['closures', id] as const,
  faqs: (params: unknown) => ['faqs', params] as const,
  faq: (id: string) => ['faq', id] as const,
  home: ['content-home'] as const,
  about: ['content-about'] as const,
  media: (params: unknown) => ['media', params] as const,
  mediaAsset: (id: string) => ['media-asset', id] as const,
  mediaUsage: (id: string) => ['media-usage', id] as const,
  settings: ['settings'] as const,
  users: (params: unknown) => ['users', params] as const,
  user: (id: string) => ['user', id] as const,
  qrTargets: ['qr-targets'] as const,
};
